import { PageDesc, RootResources } from '@fullstory/client-api';
import { assertExhaustive } from '@fullstory/typeutils';
import { CompressedResourceMap, ResourceMap, ResourceMapEntry } from './playbackcontext';
import { RequestDestinationIfKnown } from './requestdestination';

// A helper used to convert the representation of resource mappings in the Page
// object into a simpler, normalized form that can be consumed by the service
// worker.
export class ResourceMappingBuilder {
  public readonly mappings: ResourceMap = {};
  private readonly _alreadyMapped = new Set<string>();

  constructor(
    private readonly _gcsBucketUrl: string,
    private readonly _hashes: { [url: string]: string },
  ) {
    if (!this._hashes) {
      this._hashes = {}; // Tolerate a missing resource map.
    }
  }

  // Adds a mapping for the resource at the given URL of the type given by
  // `destination`. The mapping redirects loads of the given URL to resource
  // snapshots in the GCS bucket.
  addMapping(relUrl: string, baseUrl: string, destination: RequestDestinationIfKnown): void {
    if (this._alreadyMapped.has(relUrl)) {
      return;
    }

    let absUrl: URL;
    try {
      absUrl = new URL(relUrl, baseUrl);
    } catch (e) {
      console.warn(
        `Couldn't generate absolute URL for URL '${relUrl}' with base '${baseUrl}': ${e}`,
      );
      return;
    }

    // Remove any fragment identifier, just in case. (There shouldn't ever be one, though.)
    absUrl.hash = '';

    if (this._alreadyMapped.has(absUrl.href)) {
      return;
    }
    this._alreadyMapped.add(absUrl.href);
    this._alreadyMapped.add(relUrl);

    // Add a mapping if we have an entry in the resource map for this resource.
    const resourceHash = this._hashes[absUrl.href] || this._hashes[relUrl];
    if (resourceHash) {
      const resourceUrl = `${this._gcsBucketUrl}${resourceHash}`;
      const mapping: ResourceMapEntry = { to: resourceUrl };
      if (destination !== 'unknown') {
        mapping.destination = destination;
      }
      this.mappings[absUrl.href] = mapping;
    } else if (destination !== 'unknown') {
      // We weren't able to snapshot the resource, but we know what type of
      // resource it is, so add a trivial mapping. This will happen for
      // resources that are behind an auth wall or that are otherwise
      // inaccessible except in fallback mode. We do this so that we can use the
      // resource map for preloading as well. (That's why we don't bother if we
      // don't know what type of resource it is, since we can't preload in that
      // case.)
      // TODO(sethf): In the future we'll use perf events to decide what to
      // preload. At that point we can remove this code and just drop these
      // entries on the floor.
      this.mappings[absUrl.href] = {
        to: absUrl.href,
        destination,
      };
    }
  }
}

type pageWithOptionalRoots = PageDesc & { ResourceRoots?: { [baseUrl: string]: RootResources } };

export function buildResourceMap(page: pageWithOptionalRoots): ResourceMap | null {
  // Live or very new pages may not have a resource map.
  if (!page.Resources) {
    return null;
  }

  // Walk all the resource roots and add mappings for all resources.
  const builder = new ResourceMappingBuilder(page.GcsBucketUrl, page.Resources);
  for (const baseUrl in page.ResourceRoots) {
    const roots = page.ResourceRoots[baseUrl];
    if (roots.Css) {
      for (const relUrl of roots.Css) {
        builder.addMapping(relUrl, baseUrl, 'style');
      }
    }
    if (roots.Imgs) {
      for (const relUrl of roots.Imgs) {
        builder.addMapping(relUrl, baseUrl, 'image');
      }
    }
  }

  // Add mappings for any remaining URLs in the resource map. (These will
  // typically be blob URIs.) We use 'about:blank' for the base URL to ensure
  // that addResourceMapping() will fail if the URL is relative; since these
  // URLs don't appear in ResourceRoots, we don't know the real base URL.
  if (page.Resources) {
    const isMobilePage = ['android', 'ios', 'mobilemock'].includes(page.Type);
    for (const url of Object.keys(page.Resources)) {
      // Older Native Mobile cooked pages can include relative urls in their
      // Resources map (for Webview bundled assets). We need to add mappings
      // against any possible base Urls from ResourceRoots so these can resolve
      // to their uploaded GCS urls.
      // TODO(dave): Skip this code path if page.AppData.FsVersion < fixed
      // version once cook emits absolute urls.
      if (isMobilePage && url.indexOf('://') < 0) {
        for (const baseUrl in page.ResourceRoots) {
          // only do this for file scheme base urls
          if (baseUrl.startsWith('file://')) {
            builder.addMapping(url, baseUrl, 'unknown');
          }
        }
        continue;
      }

      builder.addMapping(url, 'about:blank', 'unknown');
    }
  }

  return builder.mappings;
}

/**
 * Minimal types for the Compression Streams API, which is not yet available in
 * the TypeScript standard type definitions as of 2/2021. The spec:
 *   https://wicg.github.io/compression/
 */
type WindowWithCompressionStream = Window & {
  CompressionStream?: {
    new (algorithm: 'deflate'): TransformStream;
  };
  DecompressionStream?: {
    new (algorithm: 'deflate'): TransformStream;
  };
};

/**
 * Compress the given resource map using the 'deflate' algorithm if the
 * Compression Streams API is available.
 *
 * @returns the compressed resource map in base64url-encoded form, if
 * compression is successful. Otherwise, returns null.
 */
export async function deflateResourceMap(map: ResourceMap): Promise<string | null> {
  const wnd = self as WindowWithCompressionStream;
  if (!wnd.CompressionStream) {
    return null;
  }

  // Set up a stream that will compress the data.
  const compressedStream = new Response(JSON.stringify(map)).body!.pipeThrough(
    new wnd.CompressionStream('deflate'),
  );

  // Perform the compression and store the result in a blob.
  const compressedBlob = await new Response(compressedStream).blob();

  // Convert the blob to a data URL, which will contain a base64-encoded version
  // of its content.
  const encodeDataUrl = new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (reader.result === null) {
        reject(reader.error);
        return;
      }
      resolve(reader.result as string);
    });

    reader.addEventListener('error', () => {
      reject(reader.error);
    });

    reader.readAsDataURL(compressedBlob);
  });

  try {
    const dataUrl = await encodeDataUrl;

    // Aggravatingly, a data URL is not itself URL-safe, because the base64
    // encoding it uses contains non-URL-safe characters. (In fact, there's no
    // web platform API that can generate an efficient URL-safe representation
    // of a sequence of bytes in a single step.) Because the output of this
    // function will be included in a URL, those non-URL-safe characters will
    // end up getting percent-encoded, potentially substantially increasing the
    // final size of the URL and substantially reducing the effective
    // compression ratio.
    //
    // To resolve that issue and ensure that we get the best compression ratio
    // possible, we'll munge the data URL until it contains only URL-safe
    // characters, and unmunge on the other side.

    // Step one: drop the useless scheme and content type, as well as the
    // unnecessary '=' padding at the end.
    const base64Data = dataUrl.replace(/^data:.*,/, '').replace(/=+$/, '');

    // Step two: transform the data from the traditional base64 encoding to
    // base64url. For reference, this encoding is defined here:
    //   https://tools.ietf.org/html/rfc4648#section-5
    const base64urlData = base64Data.replace(/\+/g, '-').replace(/\//g, '_');

    // Done! The resulting string is idempotent under URL-encoding.
    return base64urlData;
  } catch (e) {
    console.warn(`Failed to convert resource map to data URL: ${e}`);
    return null;
  }
}

/**
 * Decompress the given compressed resource map, which must have been generated
 * by {@link deflateResourceMap}.
 *
 * @returns the decompressed resource map in base64url-encoded form.
 */
export async function inflateResourceMap(base64urlData: string): Promise<ResourceMap> {
  const wnd = self as WindowWithCompressionStream;
  if (!wnd.DecompressionStream) {
    throw new Error(`DecompressionStream is not available, but the resource map is compressed?`);
  }

  // Invert the transformation we performed in deflateResourceMap(), converting
  // the base64url-encoded data into a data URL.
  const base64Data = base64urlData.replace(/-/g, '+').replace(/_/g, '/');
  const dataUrl = `data:application/octet-stream;base64,${base64Data}`;

  // Set up a stream that will decompress the data.
  const mapData = await fetch(dataUrl);
  const decompressedStream = mapData.body!.pipeThrough(new wnd.DecompressionStream('deflate'));

  // Perform the decompression and parse the result as JSON.
  return new Response(decompressedStream).json();
}

type EntryFilterPredicate = (href: string, destination: RequestDestination | undefined) => boolean;

/**
 * A filter for resource map entries. Either a function which returns 'true' if
 * the entry should be kept, or 'true' to keep all entries.
 */
type EntryFilter = EntryFilterPredicate | true;

/**
 * A series of progressively more restrictive filters which
 * {@link buildWithCompressedResourceMap} tries in order until the resulting
 * filtered resource map is sufficiently small. These filters, in other words,
 * are the heuristics we use for lossy resource map compression.
 */
const entryFilterFallbacks: readonly EntryFilter[] = [
  // First pass: keep all URLs.
  true,
  // Second pass: keep all CSS URLs and non-CSS URLs that don't have query params.
  (href, destination) => destination === 'style' || new URL(href).search.length === 0,
  // Third pass: keep only CSS URLs.
  (_url, destination) => destination === 'style',
];

/**
 * Filters a resource map according to the provided {@link EntryFilter}. The
 * original resource map is not mutated.
 *
 * @returns a new resource map containing only the entries that passed the
 * filter, and the number of entries that did not pass the filter and were
 * dropped.
 */
export function filterResourceMap(
  map: ResourceMap,
  keep: EntryFilter,
): [map: ResourceMap, numDroppedEntries: number] {
  // "Keep all", in the common case, is usually all we'll need. We special case
  // it because it can be handled in constant time.
  if (keep === true) {
    return [map, 0];
  }

  const filteredMap: ResourceMap = {};
  let numDroppedEntries = 0;
  for (const [href, entry] of Object.entries(map)) {
    if (keep(href, entry.destination)) {
      filteredMap[href] = entry;
    } else {
      numDroppedEntries++;
    }
  }

  return [filteredMap, numDroppedEntries];
}

export type BuildIfCompressedEnoughCallback<R> = (
  map: CompressedResourceMap | null,
  numDroppedEntries: number,
) => R | null;

/**
 * Build an object, such as a URL, that includes a resource map and has a
 * constraint on its total size. The provided factory function will be called
 * with increasingly compressed representations of the resource map until the
 * object can be built successfully. We try lossless compression first, but we
 * switch to lossy compression (i.e., removing entries from the resource map) if
 * lossless compression isn't enough.
 *
 * @param map - The resource map that will serve, in compressed form, as an
 * input to the factory function.
 * @param buildIfCompressedEnough - A factory function which builds an object
 * with a size constraint. It will be called repeatedly with increasingly
 * compressed versions of the resource map until it returns non-null or further
 * compression is impossible. It is always called at least once.
 * @returns the return value of the final call to buildIfCompressedEnough(). If
 * buildIfCompressedEnough() returns a non-null value at some point, that value
 * will be returned. Otherwise, null will be returned once no further
 * compression can occur.
 */
export async function buildWithCompressedResourceMap<R>(
  map: ResourceMap | null,
  buildIfCompressedEnough: BuildIfCompressedEnoughCallback<R>,
): Promise<R | null> {
  // If the input is null, we don't need to do any compression, but we still
  // need to call the callback.
  if (map === null) {
    return buildIfCompressedEnough(null, 0);
  }

  // Try to compress the resource map's size down until
  // buildIfCompressedEnough() returns non-null. Each pass through the loop,
  // we'll filter out more and more URLs from the map.
  for (const entryFilter of entryFilterFallbacks) {
    const [filteredMap, numDroppedEntries] = filterResourceMap(map, entryFilter);

    // If simply serializing the filtered resource map directly is enough, we'll
    // go with that, because compression has a performance cost.
    const simpleOutput = buildIfCompressedEnough(['json', filteredMap], numDroppedEntries);
    if (simpleOutput !== null) {
      return simpleOutput;
    }

    // The resource map is still too big; try running it through a lossless
    // compression algorithm. This will fail on browsers which don't support the
    // necessary APIs.
    const deflatedMap = await deflateResourceMap(filteredMap);
    if (deflatedMap === null) {
      continue;
    }

    const deflatedOutput = buildIfCompressedEnough(
      ['json+deflate', deflatedMap],
      numDroppedEntries,
    );
    if (deflatedOutput !== null) {
      return deflatedOutput;
    }
  }

  // Compression failed; fall back to an empty map.
  return buildIfCompressedEnough(['json', {}], Object.keys(map).length);
}

/**
 * Decompress a resource map which was compressed with
 * {@link buildWithCompressedResourceMap}.
 *
 * @returns the resulting resource map, or null if no resource map was provided
 * or if decompression failed.
 */
export async function decompressResourceMap(
  compressedMap: CompressedResourceMap | null | undefined,
): Promise<ResourceMap | null> {
  if (!compressedMap) {
    return null;
  }

  try {
    switch (compressedMap[0]) {
      case 'json':
        return compressedMap[1];
      case 'json+deflate':
        return await inflateResourceMap(compressedMap[1]);
      default:
        return assertExhaustive(
          compressedMap[0],
          `Unsupported resource map format: ${compressedMap[0]}`,
        );
    }
  } catch (e) {
    console.error(`Failed to decompress resource map: ${e}`);
    return null;
  }
}
